/**
 * Where the API lives, resolved once at module load.
 *
 * There are two deployed backends — staging and production — and which one a
 * bundle talks to is a deploy-time setting, not a code change: set
 * `REACT_APP_API_ENV` in the environment the build runs in, or run
 * `npm run start:staging` / `start:production` / `build:staging` /
 * `build:production`. Plain `npm run build` is production - deploys should
 * never need a flag to reach the live API. Create React App inlines
 * `REACT_APP_*` at build time, so nothing here can be changed after the fact
 * by the browser.
 *
 * `REACT_APP_API_BASE_URL` overrides both, for pointing a local build at a
 * branch deploy, a tunnel, or a backend running on localhost.
 */

// Shared with `scripts/with-api.js`, which prints the URL a build is aimed at -
// hence JSON rather than a literal here, so the two can never disagree.
import BASE_URLS from "./environments.json";

export type ApiEnvironment = keyof typeof BASE_URLS;

const ENVIRONMENTS = Object.keys(BASE_URLS) as ApiEnvironment[];

const resolveEnvironment = (): ApiEnvironment => {
  const configured = process.env.REACT_APP_API_ENV?.trim().toLowerCase();
  // Checked against the map rather than a list written out here, so adding a
  // backend is a one-line edit to the JSON and nothing else.
  const match = ENVIRONMENTS.find((name) => name === configured);
  if (match) return match;

  // Unset: only a production build should reach the live API by default. Dev
  // servers and the test runner stay on staging, so a forgotten variable can
  // never point local work at real customer data.
  return process.env.NODE_ENV === "production" ? "production" : "staging";
};

export const API_ENVIRONMENT: ApiEnvironment = resolveEnvironment();

/** Origin only, no trailing slash — `client.ts` appends paths that always
 *  begin with one, and a doubled slash is a 404 on most routers. */
export const API_BASE_URL: string = (
  process.env.REACT_APP_API_BASE_URL?.trim() || BASE_URLS[API_ENVIRONMENT]
).replace(/\/+$/, "");

/** How long a request may run before it is aborted. Long enough for a cold
 *  serverless start, short enough that a hung request still ends in an error
 *  the user can act on rather than a spinner that never stops. */
export const API_TIMEOUT_MS = 20000;
